<template>
    <div class="audio-controller">
        <a class="btn-pause btn-control"
           href="#"
           v-show="!isPaused"
           @click.prevent = "pauseAudio"
        >
            <img src="~images/btn_pause.svg">
        </a>
        <a class="btn-play btn-control"
            href="#"
            v-show="isPaused"
            @click.prevent="resumeAudio"
        >
            <img src="~images/btn_play.svg">
        </a>
        <a class="btn-stop btn-control"
           href="#"
           @click.prevent="stopAudio"
        >
            <img src="~images/btn_stop.svg">
        </a>
        <div class="playback-progress">
            <div class="timer"> {{ currentTimeDisplay }} / {{ durationDisplay }}</div>
            <slider :width="'100%'"
                    :height="'8px'"
                    :min="0"
                    :max="(karaokeState.activeAudioSrc) ? karaokeState.lastWordTime / 1000 : duration"
                    :step = "0.1"
                    :styleHandler="styleHandler"
                    @slider-sliding="onSliding"
                    @slider-slided="onSlided"
                    @slider-slide-to="onSliderSlideTo"
                    id="slider-audio-control"
                    ref="refAudioSlider"
            ></slider>
        </div>
        <div class="playrate-control-container">
            <a href="#"
               id="btn-toggle-playbackrate"
               @click.prevent="toggleRate"
            >
                {{ i18n.t("label_speed") }}
            </a>
            <div class="full-size"
                 id="playbackrate-control-mask"
                 v-if="bookInfoState.isShowRateControl"
                 @click="toggleRate"
            >
            </div>
            <div class="playbackrate-control" v-if="bookInfoState.isShowRateControl">
                <div class="label">{{ i18n.t("label_slow") }}</div>
                <div class="playbackrate-slider">
                    <slider :width="'8px'"
                            :height="'100%'"
                            :min="0"
                            :max="1"
                            :step = "0.1"
                            id="slider-playbackrate-control"
                            ref="refPlaybackRateSlider"
                            @slider-sliding="onSlidingPlaybackRate"
                            @slider-slided="onSlidedPlaybackRate"
                            @slider-slide-to="onSliderSlideToPlaybackRate"
                            :styleHandler="stylePlaybackRateHandler"
                            :vertical="true"
                    ></slider>
                </div>
                <div class="label">{{ i18n.t("label_fast") }}</div>
             </div>
        </div>
    </div>
</template>`

<script>

    import {useBookInfo} from "@/modules/reader/book/bookinfo";
    import {usePlayBlockControl} from "@/modules/reader/karaoke/playBlockControl";
    import {useMuteRoleControl} from "@/modules/reader/karaoke/muteRoleControl";
    import {useKaraokeControl} from "@/modules/reader/karaoke/karaokeControl";
    import {ref, reactive, onMounted, onUnmounted, onUpdated, watchEffect, computed, watch, nextTick, inject}  from "vue"
    import Slider from "@/views/lib/slider/Slider";
    import cssvar from '@/scss/_variables.scss';

    const colorPrimary = cssvar.colorPrimary;

    export default {
        components: {
            Slider
        },
        props: {
          playerId:String
        },
        setup(props, context) {

            const i18n = inject("i18n")
            const { bookInfoState, removeActiveAudioSrc} = useBookInfo()
            const {karaokeState , clearKaraokeAnimation, pauseAnimation, resumeAnimation, seekAudio} = useKaraokeControl();
            const styleHandler = {
                width:"12px",
                height:"15px",
                background:"#fcd549",
                color:"#000",
                fontSize:"13px",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 2px rgba(0,0,0,0.3), 0 0 4px 1px rgba(0,0,0,0.2), inset 0 3px 2px rgba(255,255,255,.22), inset 0 -3px 2px rgba(0,0,0,.15), inset 0 20px 10px rgba(255,255,255,.12), 0 0 4px 1px rgba(0,0,0,.1), 0 3px 2px rgba(0,0,0,.2)",
                border: "1px solid rgba(0,0,0,0.15)",
            }
            const stylePlaybackRateHandler = {
                width: "18px",
                height: "12px",
                background: "#fcd549",
                color: "#000",
                fontSize: "13px",
                lineHeight: "13px",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 2px rgba(0,0,0,0.3), 0 0 4px 1px rgba(0,0,0,0.2), inset 0 3px 2px rgba(255,255,255,.22), inset 0 -3px 2px rgba(0,0,0,.15), inset 0 20px 10px rgba(255,255,255,.12), 0 0 4px 1px rgba(0,0,0,.1), 0 3px 2px rgba(0,0,0,.2)",
                border: "1px solid rgba(0,0,0,0.15)",
            }

            const refAudioSlider  = ref(null);
            const refPlaybackRateSlider = ref(null);
            const currentTimeDisplay = ref("00:00");
            const durationDisplay = ref("");
            const duration = ref(0);
            const isPaused = ref(false);
            let timerId;

            const formatTimeString = (time)=> {
                let second = Math.floor(time % 60);
                let minute = Math.floor(time / 60);
                if (second < 10) {
                    second = `0${second}`;
                }
                if (minute < 10) {
                    minute = `0${minute}`;
                }
                return `${minute}:${second}`;
            }

            const timer = ()=>{

                if (! refAudioSlider.value || !document.getElementById(props.playerId) || document.getElementById(props.playerId).currentTime === duration.value) {
                    cancelAnimationFrame(timerId)
                    return;
                }

                let currentTime = Math.round(document.getElementById(props.playerId).currentTime * 10) / 10;
                refAudioSlider.value.setValue(currentTime);

                currentTimeDisplay.value = formatTimeString(document.getElementById(props.playerId).currentTime);
                timerId = window.requestAnimationFrame(timer);

            }

            onMounted(()=>{
                document.getElementById(props.playerId).addEventListener("canplay", onCanPlay);
                document.getElementById(props.playerId).addEventListener("ended",   onAudioEnded);

                //stop animation when browser tab is changed

                /*
                window.onblur = ()=>{
                    if (karaokeState.activeAudioSrc) {
                        stopAnimation();
                    }
                };*/

            })

            onUnmounted(()=>{
                window.cancelAnimationFrame(timerId);
            })

            const onCanPlay =()=> {
                nextTick(()=> {
                    if (document.getElementById(props.playerId)) {
                        //if karaoke mode, take the lastWordTime data as duration, since the physical duration is not always equal to the manual time data when authorizing karaoke.
                        if (karaokeState.activeAudioSrc) {
                            durationDisplay.value = formatTimeString(Math.floor(karaokeState.lastWordTime / 1000));
                        } else {
                            durationDisplay.value = formatTimeString(document.getElementById(props.playerId).duration);
                        }
                        duration.value = document.getElementById(props.playerId).duration
                        duration.value = Math.round(duration.value * 10) / 10;

                        bookInfoState.audioPlaybackRate = bookInfoState.audioPlaybackRate;
                        document.getElementById(props.playerId).playbackRate = bookInfoState.audioPlaybackRate;
                        timerId = window.requestAnimationFrame(timer);
                    }
                });
            }

            //TODO: move to bookinfo.js
            const toggleRate = ()=> {
                bookInfoState.isShowRateControl = !bookInfoState.isShowRateControl;
            }

            const onSliding = (e) => {

                //update the played word animation display only if karaoke is playing
                if (karaokeState.activeAudioSrc) {
                    //if seek head is slided to zero, it'll be handled separately  in onSlided event, since there is some delay when the karaoke starts,
                    if (e > 0) {
                        seekAudio(e, isPaused.value);
                    }

                }

                currentTimeDisplay.value = formatTimeString(e);
                window.cancelAnimationFrame(timerId);
            }

            const onSlided = (e) => {

                document.getElementById(props.playerId).currentTime = e;
                let currentTime = Math.round(document.getElementById(props.playerId).currentTime * 10) / 10;
                refAudioSlider.value.setValue(currentTime);

                //update the played word animation display only if karaoke is playing
                if (karaokeState.activeAudioSrc) {

                    //it is detected that at zero point, a delay is need before continuing to seekAudio, so add setTimeout
                    if (e === 0) {
                        //explicitly pause the audio
                        document.getElementById(props.playerId).pause();

                        setTimeout(()=> {
                            seekAudio(e, isPaused.value);
                            //},  karaokeState.wordAudioData[0][0]["duration"] / bookInfoState.audioPlaybackRate)
                        }, 1000);

                    } else {
                        seekAudio(e, isPaused.value);
                    }
                }

                if (!isPaused.value) {
                    timerId = window.requestAnimationFrame(timer);
                }

            }

            const onSliderSlideTo = (e) => {
                onSlided(e);
            }

            const onSlidingPlaybackRate = (e) => {
                //playbackRate slider min and max is 0 and 1 respectively. set 0.5 to make the value center.
                //html5 audio playbackRate is 1 by default. increase/decrease 0.5 to change the rate.
                bookInfoState.audioPlaybackRate = e + 0.5;
                document.getElementById(props.playerId).playbackRate = e + 0.5;
            }

            const onSlidedPlaybackRate = (e) => {
                //playbackRate slider min and max is 0 and 1 respectively. set 0.5 to make the value center.
                //html5 audio playbackRate is 1 by default. increase/decrease 0.5 to change the rate.
                bookInfoState.audioPlaybackRate = e + 0.5;
                document.getElementById(props.playerId).playbackRate = e + 0.5;
            }

            const onSliderSlideToPlaybackRate = (e) => {
                onSlidedPlaybackRate(e);
            }

            const stopAudio = ()=>{
                isPaused.value = false;
                document.getElementById(props.playerId).pause();
                document.getElementById(props.playerId).currentTime = 0;

                usePlayBlockControl().reset();
                useMuteRoleControl().reset();
                useKaraokeControl().reset();

                onAudioEnded();
            }

            const pauseAudio = ()=>{
                isPaused.value = true;
                document.getElementById(props.playerId).pause();

                window.cancelAnimationFrame(timerId);
                if (karaokeState.activeAudioSrc) {
                    pauseAnimation();
                }
            }

            const resumeAudio = ()=> {
                isPaused.value = false;
                document.getElementById(props.playerId).play();

                timerId = window.requestAnimationFrame(timer);
                if (karaokeState.activeAudioSrc) {
                    resumeAnimation();
                }
            }

            const onAudioEnded = ()=>{

                window.cancelAnimationFrame(timerId);

                //remove simple individual mp3 audio
                if (bookInfoState.activeAudioSrc) {
                    removeActiveAudioSrc()
                }

                /*
                //remove karaoke data
                if (karaokeState.activeAudioSrc) {
                    stopAnimation();
                }*/

            }

            watchEffect(()=>{

                if (bookInfoState.isShowRateControl) {
                    nextTick(()=> {
                        //playbackRate slider min and max is 0 and 1 respectively. set 0.5 to make the value center.
                        //html5 audio playbackRate is 1 by default. increase/decrease 0.5 to change the rate.
                        if (refPlaybackRateSlider.value) {
                            refPlaybackRateSlider.value.setValue(bookInfoState.audioPlaybackRate - 0.5);
                        }
                    });
                }

                //reset play/pause btn state when new audio is loaded
                if (karaokeState.activeAudioSrc) {
                    isPaused.value = false;
                }
            })

            return {
                i18n,
                karaokeState,
                bookInfoState,
                isPaused,
                styleHandler,
                stylePlaybackRateHandler,
                refAudioSlider,
                refPlaybackRateSlider,
                currentTimeDisplay,
                durationDisplay,
                duration,
                stopAudio,
                resumeAudio,
                pauseAudio,
                onCanPlay,
                onSliding,
                onSlided,
                onSliderSlideTo,
                onSlidingPlaybackRate,
                onSlidedPlaybackRate,
                onSliderSlideToPlaybackRate,
                toggleRate
            }

        },

    }

</script>

<style scoped lang="scss">

    .audio-controller  {
        background:$color-primary-deepest;
        height:26px;
        padding:8px;
        border-radius:6px;
        display:flex;
        align-items:center;
        font-size:14px;
    }

    .btn-play {

    }

    .btn-control {
        width:24px;
        height:24px;
    }

    .timer {
        font-size:13px;
        position:relative;
        top:2px;
        margin: -6px 0 0px 0;
        color:#fff;
    }

    .playback-progress {
        width:180px;
        padding:4px;
        margin:0 8px;
        text-align:center;
    }

    #btn-toggle-playbackrate {
        color:#fff;
        display:block;
        margin:0 4px;
        min-width:36px;
        text-align:center;
    }

    .layrate-control-container {
        text-align:center;
    }

    .playbackrate-control {
        background: transparentize($color-primary-deepest, 0.3);
        width: 40px;
        height: 180px;
        display: block;
        padding: 0 0 10px 0;
        position: absolute;
        top:46px;
        overscroll-behavior:none;

        .label {
            text-align:center;
            width:100%;
            padding:4px 0;
            color:#fff;
        }

        .playbackrate-slider {
            width: 100%;
            height: 120px;
            margin: 8px 9px;
        }
    }

    #playbackrate-control-mask {
        position:fixed;
        background:transparent;
        left:0;
        top:$header-height;
        touch-action:none;
    }




</style>